<template>
    <label v-if="!file"
        class="p-8 border-dashed border-2 rounded-[16px] flex flex-col items-center justify-center text-center relative cursor-pointer"
        :class="{
            'border-sky-500 bg-white': dragging,
            'border-slate-300 bg-slate-100': !dragging,
        }"
        data-test="drop-file"
        @dragenter.prevent="dragging = true"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onFileDropped">
        <input type="file"
            @change="onFileAdded"
            class="absolute opacity-0"
            :accept="props.accept" />

        <font-awesome-icon widthAuto :icon="props.icon"
            size="3x"
            class="text-sky-400"></font-awesome-icon>
        <div class="font-semibold mt-2">Drag media here</div>
        <div class="mb-3 mt-2">— or —</div>

        <!-- pointer-events-none on this button is necessary for the parent label to work -->
        <vx-button color="primary"
            size="sm"
            type="button"
            class="pointer-events-none">
            Browse Computer
            <font-awesome-icon widthAuto class="text-sm"
                :icon="faFileMedical"></font-awesome-icon>
        </vx-button>
    </label>
    <div v-else>
        <div class="my-4 p-4 rounded-[16px] bg-white flex items-center gap-4 relative">
            <font-awesome-icon widthAuto :icon="props.icon"
                size="3x"
                class="text-slate-400"></font-awesome-icon>
            <div data-test="drop-file-name"
                class="text-slate-500 font-normal text-lg break-all">
                {{ file.name }}
            </div>
            <button @click="setFile(undefined)"
                type="button"
                class="bg-rose-400 rounded-full text-center flex items-center justify-center p-0 appearance-none border-solid border-[5px] border-slate-50 w-9 h-9 absolute -inset-e-4 -inset-bs-4"
                data-test="remove-file"
                aria-label="Remove file">
                <font-awesome-icon fixedWidth :icon="faTrash"
                    class="text-sm text-white"></font-awesome-icon>
            </button>
        </div>

        <vx-alert v-if="!validation"
            color="primary"
            :closeable="false"> Validating... </vx-alert>
        <div v-else>
            <vx-alert v-if="validation.message"
                class="mt-4 break-all"
                color="danger"
                :closeable="false"
                data-test="drop-file-error">
                {{ validation.message }}
            </vx-alert>

            <vx-alert v-else
                color="primary"
                :closeable="false"
                data-test="drop-file-valid">
                <strong>Ready</strong> to upload with
                {{ formatNumber(validation.validCount) }} rows
            </vx-alert>
        </div>
    </div>
</template>
<script setup>
import { faFile, faFileMedical, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxAlert } from '@voxie/frontend-components';
import { ref } from 'vue';
import { formatNumber } from '~/utils/helpers';

const props = defineProps({
    icon: {
        type: Object,
        default: () => faFile
    },
    accept: {
        type: String,
    },
    validate: {
        type: Function,
        required: true,
        default() {
            return Promise.reject({ message: 'No validations were executed.' });
        }
    }
});

const emit = defineEmits(['selected']);

const dragging = ref(false);

const file = defineModel();

const validation = ref(undefined);

/**
 * @param {File|undefined} fileEvent
 */
const setFile = async (fileEvent) => {
    file.value = fileEvent;

    if (!fileEvent) {
        validation.value = undefined;
        emit('selected', undefined);
        return;
    }

    try {
        validation.value = await props.validate(fileEvent);

        if (!validation.value.message) {
            emit('selected', fileEvent);
        }
    } catch (e) {
        validation.value = {
            message: e.message,
        };
    }
};

const onFileDropped = (event) => {
    dragging.value = false;

    if (!event.dataTransfer.files[0]) {
        return;
    }

    setFile(event.dataTransfer.files[0]);
};

const onFileAdded = async (event) => {
    if (!event.target.files[0]) {
        return;
    }

    try {
        await setFile(event.target.files[0]);
    } finally {
        event.target.value = '';
    }
};
</script>
